import Phaser from 'phaser';
import { COLORS, intToHex } from '../utils/constants';
import { Player } from '../classes/Player';
import { Boss } from '../classes/Boss';
import { ChallengeSystem } from '../classes/ChallengeSystem';
import { HUD } from '../classes/HUD';
import { HeadTracker } from '../systems/HeadTracker';
import { BodyTracker } from '../systems/BodyTracker';
import { CameraOverlay } from '../systems/CameraOverlay';
import { ComboFlash } from '../systems/ComboFlash';
import { eventBus } from '../systems/EventBus';
import { soundManager } from '../systems/SoundManager';
import { weaponSystem } from '../systems/WeaponSystem';
import { walletManager } from '../systems/WalletManager';

const ARENA_W = 2400;
const ARENA_H = 1400;

export default class BattleScene extends Phaser.Scene {
  private player!: Player;
  private boss!: Boss;
  private challengeSystem!: ChallengeSystem;
  private hud!: HUD;
  private headTracker: HeadTracker | null = null;
  private bodyTracker: BodyTracker | null = null;
  private cameraOverlay: CameraOverlay | null = null;
  private comboFlash: ComboFlash | null = null;
  private keyboardOnly = false;
  private useCamera = false;
  private useKeyboard = true;
  private battleActive = false;
  private paused = false;
  private pauseOverlay: Phaser.GameObjects.Container | null = null;
  private floorY = 0;
  private bgGraphics!: Phaser.GameObjects.Graphics;
  private floorGraphics!: Phaser.GameObjects.Graphics;
  private wallGraphics!: Phaser.GameObjects.Graphics;
  private learnTimer: Phaser.Time.TimerEvent | null = null;
  private difficultyTimer: Phaser.Time.TimerEvent | null = null;
  private recentPlayerActions: { time: number; type: string }[] = [];
  private lastCombo = 0;

  constructor() { super({ key: 'BattleScene' }); }

  private playerName = '';

  init(data: any) {
    this.headTracker = data.headTracker || null;
    this.keyboardOnly = data.keyboardOnly ?? true;
    this.playerName = data.playerName || '';
    this.useCamera = data.useCamera ?? !!this.headTracker;
    this.useKeyboard = data.useKeyboard ?? true;
  }

  create() {
    this.floorY = ARENA_H - 100;
    this.physics.world.setBounds(0, 0, ARENA_W, ARENA_H);

    this.createArena();
    this.createDecorations();

    this.player = new Player(this, 400, this.floorY - 30, this.headTracker);
    this.player.setKeyboardOnly(this.keyboardOnly);

    this.boss = new Boss(this, ARENA_W - 400, this.floorY - 40);
    this.challengeSystem = new ChallengeSystem(this);
    this.hud = new HUD(this);

    // Combo flash
    this.comboFlash = new ComboFlash();

    // Camera overlay
    if (this.useCamera && this.headTracker) {
      this.cameraOverlay = new CameraOverlay();
      const video = this.headTracker.getVideoElement();
      if (video) {
        this.cameraOverlay.setVideoElement(video);
        this.cameraOverlay.show();
        this.cameraOverlay.drawFrame();
      }

      // Body tracker shares the same camera
      if (this.useCamera) {
        this.bodyTracker = new BodyTracker();
        const v = this.headTracker.getVideoElement();
        if (v) this.bodyTracker.init(v);
      }
    }

    this.cameras.main.setBounds(0, 0, ARENA_W, ARENA_H);
    this.cameras.main.startFollow(this.player.sprite, true, 0.08, 0.08);
    this.cameras.main.setDeadzone(100, 50);

    this.setupEvents();

    this.time.delayedCall(1000, () => {
      this.battleActive = true;
      this.challengeSystem.start();
      this.startAILearning();
      this.startDifficultyAdjustment();
    });

    this.input.keyboard!.on('keydown', (event: KeyboardEvent) => {
      if (!this.battleActive || this.paused) return;
      this.challengeSystem.handleKeyInput(event.code);
    });

    this.input.keyboard!.on('keydown-Q', () => {
      if (!this.battleActive || this.paused) return;
      const w = weaponSystem.switchNext();
      soundManager.play('ui_click');
      this.hud.showFloatingText(this.player.sprite.x, this.player.sprite.y - 50, `${w.icon} ${w.name}`, '#ffea00');
    });
    this.input.keyboard!.on('keydown-E', () => {
      if (!this.battleActive || this.paused) return;
      const w = weaponSystem.switchPrev();
      soundManager.play('ui_click');
      this.hud.showFloatingText(this.player.sprite.x, this.player.sprite.y - 50, `${w.icon} ${w.name}`, '#ffea00');
    });
    this.input.keyboard!.on('keydown-ESC', () => this.togglePause());
    this.events.on('shutdown', this.shutdown, this);

    this.showIntro();
    soundManager.play('battle_start');
  }

  private createArena() {
    this.bgGraphics = this.add.graphics().setDepth(0).setScrollFactor(1);
    this.bgGraphics.fillGradientStyle(0x050510, 0x0a0a2a, 0x0a0a1a, 0x1a0a2a);
    this.bgGraphics.fillRect(0, 0, ARENA_W, ARENA_H);

    this.bgGraphics.fillStyle(0x0a0a2a, 0.5);
    for (let i = 0; i < 30; i++) {
      const bx = (i / 30) * ARENA_W;
      this.bgGraphics.fillRect(bx, this.floorY - (120 + Math.random() * 250), 40 + Math.random() * 80, 140 + Math.random() * 250);
    }
    this.bgGraphics.fillStyle(COLORS.cyan, 0.06);
    for (let i = 0; i < 60; i++) {
      this.bgGraphics.fillRect(Math.random() * ARENA_W, 100 + Math.random() * (this.floorY - 160), 4 + Math.random() * 8, 3 + Math.random() * 6);
    }

    this.floorGraphics = this.add.graphics().setDepth(1).setScrollFactor(1);
    this.floorGraphics.fillStyle(COLORS.bg, 1);
    this.floorGraphics.fillRect(0, this.floorY + 20, ARENA_W, ARENA_H - this.floorY);

    this.wallGraphics = this.add.graphics().setDepth(3).setScrollFactor(1);
    this.wallGraphics.lineStyle(4, COLORS.pink, 0.3);
    this.wallGraphics.strokeRect(0, 0, ARENA_W, ARENA_H);
    this.wallGraphics.lineStyle(2, COLORS.cyan, 0.15);
    this.wallGraphics.strokeRect(20, 20, ARENA_W - 40, ARENA_H - 40);

    // 战斗场景粒子背景
    if (this.textures.exists('glow')) {
      this.add.particles(ARENA_W / 2, this.floorY / 2, 'glow', {
        lifespan: 5000,
        speed: { min: 5, max: 25 },
        angle: { min: 250, max: 290 },
        scale: { start: 0.2, end: 0 },
        alpha: { start: 0.15, end: 0 },
        blendMode: Phaser.BlendModes.ADD,
        quantity: 1,
        frequency: 500,
        tint: [COLORS.cyan, COLORS.purple],
      }).setDepth(0).setScrollFactor(1);
    }
  }

  private createDecorations() {
    const d = this.add.graphics().setDepth(1).setScrollFactor(1);
    d.lineStyle(1, COLORS.cyan, 0.04);
    for (let x = 0; x < ARENA_W; x += 100) d.lineBetween(x, this.floorY, x, ARENA_H);
    for (let y = this.floorY; y < ARENA_H; y += 100) d.lineBetween(0, y, ARENA_W, y);
    d.lineStyle(1, COLORS.cyan, 0.06);
    d.lineBetween(0, this.floorY, ARENA_W, this.floorY);

    [600, 1200, 1800, 300, 2100].forEach(x => {
      const py = this.floorY - 20;
      d.fillStyle(0x000000, 0.2);
      d.fillEllipse(x + 8, this.floorY + 8, 50, 16);
      d.fillStyle(COLORS.bgPanel, 1);
      d.fillRect(x - 12, this.floorY - 80, 24, 80);
      d.fillStyle(COLORS.cyan, 0.3);
      d.fillRect(x - 12, this.floorY - 80, 24, 4);
      d.fillRect(x - 12, this.floorY - 4, 24, 4);
      d.fillStyle(COLORS.cyan, 0.15);
      d.fillRect(x - 16, this.floorY - 84, 32, 6);
    });

    d.lineStyle(2, COLORS.purple, 0.1);
    d.strokeCircle(ARENA_W / 2, this.floorY - 20, 300);
  }

  private updateHandler: (() => void) | null = null;

  private setupEvents() {
    this.events.on('challenge-success', () => {
      soundManager.play('challenge_success');
      this.hud.addScore(100);
      this.player.heal(5);
    });
    this.events.on('challenge-fail', () => soundManager.play('challenge_fail'));
    this.events.on('boss-defeated', () => this.endBattle(true));
    this.updateHandler = () => {
      if (!this.battleActive) return;
      this.checkCombat();
      this.checkDeath();
      this.processBodyTracking();
      this.updateOverlay();
    };
    this.events.on('update', this.updateHandler);
  }

  private checkCombat() {
    const hitbox = this.player.getAttackHitbox();
    if (hitbox && this.boss.isAlive) {
      const dist = Phaser.Math.Distance.Between(hitbox.x, hitbox.y, this.boss.sprite.x, this.boss.sprite.y);
      const at = this.player.getAttackType();
      if (dist < 55) {
        const dmg = at === 'skill' ? 15 : at === 'heavy' ? 12 : 8;
        this.boss.takeDamage(dmg, at || 'normal');
        this.player.incrementCombo();
        this.hud.addScore(at === 'skill' ? 50 : at === 'heavy' ? 30 : 10);
        soundManager.play('boss_hit');
        this.player.stats.hitsLanded++;
        this.recentPlayerActions.push({ time: this.time.now, type: 'attack' });
        this.floatingText(this.boss.sprite.x, this.boss.sprite.y - 40, at === 'skill' ? '+15' : at === 'heavy' ? '+12' : '+8');
        this.checkComboFlash();
      }
    }

    const projs = this.boss.getProjectiles();
    for (const p of projs) {
      if (!p.active) continue;
      if (Phaser.Math.Distance.Between(p.x, p.y, this.player.sprite.x, this.player.sprite.y) < 30) {
        const dmg = (p as any)._damage || 5;
        if (this.player.takeDamage(dmg)) { p.destroy(); this.floatingText(this.player.sprite.x, this.player.sprite.y - 30, `-${dmg}`, '#ff1744'); }
      }
    }

    if (this.boss.isCharging() && Phaser.Math.Distance.Between(this.boss.sprite.x, this.boss.sprite.y, this.player.sprite.x, this.player.sprite.y) < 45) {
      if (this.player.takeDamage(15)) this.floatingText(this.player.sprite.x, this.player.sprite.y - 30, '-15', '#ff1744');
    }
  }

  private checkComboFlash() {
    const combo = this.player.stats.combo;
    if (combo > 1 && combo !== this.lastCombo) {
      this.lastCombo = combo;
      if (combo >= 5) {
        const labels = ['5x 连击!', '10x 连续暴击!', '20x 超级连击!', '30x 史诗连击!!', '50x 传说连击!!!'];
        const idx = combo >= 50 ? 4 : combo >= 30 ? 3 : combo >= 20 ? 2 : combo >= 10 ? 1 : 0;
        const damage = combo >= 10 ? `伤害 +${combo * 5}%` : '';
        this.comboFlash?.show(combo, damage);
      }
    }
  }

  private checkDeath() {
    if (!this.player.isAlive()) this.endBattle(false);
  }

  private processBodyTracking() {
    if (!this.bodyTracker || !this.battleActive || this.paused) return;
    const g = this.bodyTracker.getGesture();
    const lms = this.bodyTracker.getLandmarks();

    // Update skeleton in camera overlay
    if (lms) this.cameraOverlay?.updateSkeleton(lms);

    // Map gestures to player attacks
    if (g.punching) {
      this.player.fireAttack('normal');
      if (this.cameraOverlay) this.cameraOverlay.showAction('👊 出拳!');
    } else if (g.bothArmsUp && !g.armsCrossed) {
      this.player.fireAttack('skill');
      if (this.cameraOverlay) this.cameraOverlay.showAction('⬆️ 技能!');
    } else if (g.armsCrossed) {
      this.player.fireAttack('shield');
      if (this.cameraOverlay) this.cameraOverlay.showAction('🛡️ 护盾!');
    } else if (g.waving) {
      this.player.fireAttack('dash');
      if (this.cameraOverlay) this.cameraOverlay.showAction('👋 冲刺!');
    }
  }

  private updateOverlay() {
    // Update camera overlay position (responsive)
  }

  private floatingText(x: number, y: number, text: string, color = '#00f0ff') {
    const ft = this.add.text(x, y - 20, text, {
      fontFamily: 'Arial', fontSize: '16px', color, fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(55);
    this.tweens.add({ targets: ft, y: y - 60, alpha: 0, duration: 800, onComplete: () => ft.destroy() });
  }

  private startAILearning() {
    this.learnTimer = this.time.addEvent({
      delay: 5000, loop: true,
      callback: () => {
        const recent = this.recentPlayerActions.filter(a => this.time.now - a.time < 5000);
        const left = recent.filter(a => a.type === 'move_left').length;
        const right = recent.filter(a => a.type === 'move_right').length;
        const atk = recent.filter(a => a.type === 'attack').length;
        this.boss.learnFromPlayer({
          leftDodges: left, rightDodges: right, attackFrequency: atk,
          skillUsage: this.player.stats.skillUses, dodgeCount: this.player.stats.dodges,
          damageTaken: this.player.stats.damagesTaken, playerDamageDealt: this.player.stats.hitsLanded,
          aggressiveMoves: atk, defensiveMoves: this.player.stats.shieldUses + this.player.stats.dodges,
        });
        this.recentPlayerActions = this.recentPlayerActions.filter(a => this.time.now - a.time < 5000);
      },
    });
  }

  private startDifficultyAdjustment() {
    this.difficultyTimer = this.time.addEvent({
      delay: 10000, loop: true,
      callback: () => {
        const rate = this.challengeSystem.getCompletionRate();
        if (rate > 0.7) { this.challengeSystem.setDifficulty(this.boss.phase * 0.5 + 1.2); this.challengeSystem.setMaxActive(this.boss.phase + 1); }
        else if (rate < 0.3) { this.challengeSystem.setDifficulty(0.8); this.challengeSystem.setMaxActive(1); }
      },
    });
  }

  private showIntro() {
    const cam = this.cameras.main;
    const cx = cam.scrollX + cam.width / 2, cy = cam.scrollY + cam.height / 2;
    const t = this.add.text(cx, cy - 50, '战斗开始', {
      fontFamily: 'Microsoft YaHei, Arial Black, Arial, sans-serif',
      fontSize: '64px', color: '#00f0ff', stroke: '#1a8fff', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(60).setAlpha(0);
    this.tweens.add({ targets: t, alpha: 1, scaleX: 1.2, scaleY: 1.2, duration: 500, yoyo: true, hold: 500, onComplete: () => t.destroy() });
  }

  private togglePause() {
    this.paused = !this.paused;
    const cam = this.cameras.main;
    const cx = cam.scrollX + cam.width / 2, cy = cam.scrollY + cam.height / 2;

    if (this.paused) {
      this.physics.pause();
      this.pauseOverlay = this.add.container(cx, cy).setDepth(100);
      this.pauseOverlay.add([
        this.add.rectangle(cx, cy, cam.width, cam.height, 0x000000, 0.6).setOrigin(0.5),
        this.add.text(cx, cy - 80, '暂停', { fontFamily: 'Microsoft YaHei, Arial, sans-serif', fontSize: '48px', color: '#00f0ff', fontStyle: 'bold' }).setOrigin(0.5),
        this.add.text(cx, cy + 10, '按 ESC 继续', { fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '18px', color: '#555577' }).setOrigin(0.5),
      ]);
      const q = this.add.text(cx, cy + 60, '退出战斗', { fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '20px', color: '#ff1744', fontStyle: 'bold' }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      q.on('pointerdown', () => { soundManager.play('ui_click'); this.physics.resume(); this.endBattle(false); });
      q.on('pointerover', () => q.setColor('#ff6b8a'));
      q.on('pointerout', () => q.setColor('#ff1744'));
      this.pauseOverlay.add(q);
    } else {
      this.physics.resume();
      this.pauseOverlay?.destroy();
      this.pauseOverlay = null;
    }
  }

  private endBattle(won: boolean) {
    this.battleActive = false;
    this.paused = false;
    this.pauseOverlay?.destroy();
    this.pauseOverlay = null;
    this.physics.resume();
    soundManager.play(won ? 'victory' : 'defeat');
    this.learnTimer?.remove();
    this.difficultyTimer?.remove();
    this.challengeSystem.stop();
    this.cameraOverlay?.hide();

    const bt = this.hud.getBattleTime();
    const sc = this.hud.getScore();
    const cs = this.challengeSystem.getStats();
    const hr = this.player.stats.hitsLanded + this.player.stats.hitsMissed > 0
      ? this.player.stats.hitsLanded / (this.player.stats.hitsLanded + this.player.stats.hitsMissed) : 0;

    this.cameras.main.fadeOut(1000, 0, 0, 0);
    this.time.delayedCall(1000, () => {
      walletManager.earnFromBattle(won, this.hud.getScore(), this.player.stats.maxCombo);

      // 金币奖励动画
      const earned = Math.floor(this.hud.getScore() / 10) + (won ? 100 : 0);
      const gems = (won ? 1 : 0) + (this.hud.getScore() >= 80 ? 1 : 0);
      const cx = this.cameras.main.width / 2;
      const cy = this.cameras.main.height / 2;
      const rewardText = this.add.text(cx, cy - 30, `💰 +${earned} 金币`, {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '28px', color: '#ffea00',
        fontStyle: 'bold', stroke: '#000000', strokeThickness: 4,
      }).setOrigin(0.5).setDepth(200).setAlpha(0).setScrollFactor(0);
      this.tweens.add({
        targets: rewardText, alpha: 1, y: cy - 50, duration: 500,
        ease: 'Back.easeOut',
      });
      if (gems > 0) {
        const gemText = this.add.text(cx, cy + 10, `💎 +${gems} 宝石`, {
          fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '20px', color: intToHex(COLORS.cyan),
          fontStyle: 'bold', stroke: '#000000', strokeThickness: 3,
        }).setOrigin(0.5).setDepth(200).setAlpha(0).setScrollFactor(0);
        this.tweens.add({ targets: gemText, alpha: 1, duration: 400, delay: 300 });
      }

      this.time.delayedCall(1500, () => {
        this.scene.start('AnalysisScene', {
          won, score: sc, battleTime: bt, playerStats: this.player.stats,
          challengeStats: cs, hitRate: hr, bossPhase: this.boss.phase,
          playerName: this.playerName, headTracker: this.headTracker,
          keyboardOnly: this.keyboardOnly, useCamera: this.useCamera, useKeyboard: this.useKeyboard,
        });
      });
    });
  }

  update(time: number, delta: number) {
    if (!this.battleActive) return;
    this.player.update(time, delta);
    this.boss.update(time, delta, this.player.sprite.x, this.player.sprite.y);
    this.hud.update(this.player, this.boss, this.challengeSystem.getCompletionRate());
  }

  shutdown() {
    this.player?.destroy();
    this.boss?.destroy();
    this.challengeSystem?.destroy();
    this.hud?.destroy();
    this.bodyTracker?.stop();
    this.cameraOverlay?.hide();
    if (this.updateHandler) {
      this.events.off('update', this.updateHandler);
      this.updateHandler = null;
    }
  }
}
